/**
 * I listini dei fornitori per la console (atlante v2, cap. 435). Si LEGGONO da Firestore (il super utente legge tutto,
 * regola di piattaforma); si SCRIVONO solo dalle funzioni del server: `listinoControllaFonte` (controlla la fonte
 * adesso, senza aspettare il giro del mattino) e `listinoPubblicaVersione` (pubblica la bozza).
 */
import { collection, doc, getDoc, getDocs, type Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

export type MotivoDaVerificare = 'codice_cambiato' | 'piu_prezzi' | 'oltre_soglia';

export interface VoceDelConfronto {
  id: string;
  codice: string | null;
  nome?: string | null;
  prima: number | null;
  dopo?: number;
  motivo?: MotivoDaVerificare;
  candidati?: Array<{ codice: string; prezzi: number[] }>;
  prezzi_trovati?: number[];
  /** Le versioni preparate prima del 28/10/2026 sera hanno solo il testo. */
  perche?: string;
  trovati?: string[];
}

export interface VociDelConfronto {
  aumentati: VoceDelConfronto[];
  scesi: VoceDelConfronto[];
  spariti: VoceDelConfronto[];
  da_verificare: VoceDelConfronto[];
}

export interface FonteDellaVersione {
  tipo: string;
  riferimento?: string;
  file_id?: string;
  data_documento?: string | null;
}

export interface Riepilogo {
  aumentati?: number;
  scesi?: number;
  invariati?: number;
  spariti?: number;
  da_verificare?: number;
  nuovi?: number;
}

export interface BozzaDelListino {
  numero: number;
  fonte: FonteDellaVersione;
  riepilogo: Riepilogo;
  voci: VociDelConfronto;
  creata_il: Date | null;
  prezzi_da_scrivere: number;
  spariti: number;
}

export interface VersionePubblicata {
  numero: number;
  fonte: FonteDellaVersione;
  riepilogo: Riepilogo;
  avvisa_amministratori: boolean | null;
  pubblicata_il: Date | null;
}

export interface ListinoDelFornitore {
  fornitore_id: string;
  nome: string;
  fonte: { tipo: string; cartella_id?: string; nome_file?: string } | null;
  controllato_il: Date | null;
  problema: string | null;
  ultima_pubblicata: number;
  bozza: BozzaDelListino | null;
  versioni: VersionePubblicata[];
}

type Mappa = Record<string, unknown>;
const data = (v: unknown): Date | null => (v && typeof (v as Timestamp).toDate === 'function' ? (v as Timestamp).toDate() : null);
const vociVuote: VociDelConfronto = { aumentati: [], scesi: [], spariti: [], da_verificare: [] };
const voci = (v: unknown): VociDelConfronto => ({ ...vociVuote, ...((v ?? {}) as Partial<VociDelConfronto>) });

/**
 * I prezzi cambiati a mano dal fornitore (cap. 437) entrano fra quelli che salgono o scendono, al posto della voce letta
 * dal listino per lo stesso prodotto: alla pubblicazione vince il prezzo a mano.
 */
function conIPrezziAMano(v: VociDelConfronto, aMano: VoceDelConfronto[]): VociDelConfronto {
  const ids = new Set(aMano.map((r) => r.id));
  const senza = (l: VoceDelConfronto[]) => l.filter((r) => !ids.has(r.id));
  return {
    ...v,
    aumentati: [...senza(v.aumentati), ...aMano.filter((r) => (r.dopo ?? 0) > (r.prima ?? 0))],
    scesi: [...senza(v.scesi), ...aMano.filter((r) => (r.dopo ?? 0) < (r.prima ?? 0))],
  };
}

export async function leggiListiniDeiFornitori(): Promise<ListinoDelFornitore[]> {
  const inizio = performance.now();
  const snap = await getDocs(collection(db, 'forniture_listini'));
  const listini = await Promise.all(snap.docs.map(async (d): Promise<ListinoDelFornitore> => {
    const x = d.data() as Mappa;
    const ultima = Number(x.ultima_pubblicata ?? 1);
    const [tenant, versioniSnap, bozzaSnap] = await Promise.all([
      getDoc(doc(db, 'tenants', d.id)),
      getDocs(collection(d.ref, 'versioni')),
      getDoc(doc(d.ref, 'bozze', String(ultima + 1))),
    ]);
    const b = bozzaSnap.exists() ? (bozzaSnap.data() as Mappa) : null;
    const visto = (x.ultimo_visto ?? null) as Mappa | null;
    return {
      fornitore_id: d.id,
      nome: String(tenant.get('nome_azienda') ?? d.id),
      fonte: (x.fonte ?? null) as ListinoDelFornitore['fonte'],
      controllato_il: data(visto?.controllato_il),
      problema: typeof visto?.problema === 'string' ? visto.problema : null,
      ultima_pubblicata: ultima,
      bozza: b
        ? {
            numero: Number(b.numero),
            fonte: (b.fonte ?? { tipo: 'sconosciuta' }) as FonteDellaVersione,
            riepilogo: (b.riepilogo ?? {}) as Riepilogo,
            voci: conIPrezziAMano(voci(b.voci), Array.isArray(b.a_mano) ? (b.a_mano as VoceDelConfronto[]) : []),
            creata_il: data(b.creata_il),
            prezzi_da_scrivere: new Set([
              ...(Array.isArray(b.da_scrivere) ? (b.da_scrivere as Array<{ id: string }>).map((r) => r.id) : []),
              ...(Array.isArray(b.a_mano) ? (b.a_mano as Array<{ id: string }>).map((r) => r.id) : []),
            ]).size,
            spariti: Array.isArray(b.spariti_ids) ? b.spariti_ids.length : 0,
          }
        : null,
      versioni: versioniSnap.docs
        .map((v) => {
          const y = v.data() as Mappa;
          return {
            numero: Number(y.numero),
            fonte: (y.fonte ?? { tipo: 'sconosciuta' }) as FonteDellaVersione,
            riepilogo: (y.riepilogo ?? {}) as Riepilogo,
            avvisa_amministratori: typeof y.avvisa_amministratori === 'boolean' ? y.avvisa_amministratori : null,
            pubblicata_il: data(y.pubblicata_il) ?? data(y.creata_il),
          };
        })
        .sort((a, b2) => b2.numero - a.numero),
    };
  }));
  console.info(`[listini] ${listini.length} listini letti in ${Math.round(performance.now() - inizio)} ms`);
  return listini.sort((a, b) => a.nome.localeCompare(b.nome));
}

async function chiama<I, O>(nome: string, dati: I): Promise<O> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<I, O>(functions, nome, { timeout: 300_000 })(dati);
    console.info(`[listini] ${nome} ok in ${Math.round(performance.now() - inizio)} ms`, r.data);
    return r.data;
  } catch (err) {
    console.error(`[listini] ${nome} fallita dopo ${Math.round(performance.now() - inizio)} ms`, err);
    throw err;
  }
}

export type EsitoDelControllo =
  | { esito: 'senza_fonte' | 'file_non_trovato' | 'invariato'; fornitore: string }
  | { esito: 'bozza'; fornitore: string; versione: number }
  | { esito: 'errore'; fornitore: string; motivo: string };

export const controllaLaFonte = (fornitore: string) =>
  chiama<{ fornitore_tenant_id: string }, EsitoDelControllo>('listinoControllaFonte', { fornitore_tenant_id: fornitore });

export interface EsitoPubblicazione {
  versione: number;
  prodotti_aggiornati: number;
  spariti: number;
  avvisa_amministratori: boolean;
}

export const pubblicaLaVersione = (fornitore: string, versione: number, avvisa: boolean) =>
  chiama<{ fornitore_tenant_id: string; versione: number; avvisa_amministratori: boolean }, EsitoPubblicazione>('listinoPubblicaVersione', {
    fornitore_tenant_id: fornitore,
    versione,
    avvisa_amministratori: avvisa,
  });

/** Il messaggio di un errore delle funzioni, così com'è scritto dal server. */
export function motivoErrore(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
